import { Box, IconButton, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import React from 'react';
import { CheckIcon } from '@/components';
import EnumTranslate from '@/components/HelperComponents/EnumTranslate';
import Delete from '@/components/SVG/Delete';
import Edit from '@/components/SVG/Edit';
import { StateDTO, TerminalState } from '@/model';
import { TranslationFunction } from '@/type';

const TERMINAL_STATE_CLASS_NAMES: Record<TerminalState, string> = {
  [TerminalState.NORMAL]: 'state-color-normal',
  [TerminalState.OFFLINE]: 'state-color-offline',
  [TerminalState.LOW_POWER]: 'state-color-lowpower',
  [TerminalState.NO_POWER]: 'state-color-nopower',
  [TerminalState.INCOMPLETE]: 'state-color-incomplet',
};

/** The weekday columns only mark whether the state applies on that day. */
const dayColumn = (field: keyof StateDTO, headerName: string): GridColDef<StateDTO> => ({
  field,
  headerName,
  flex: 1,
  filterable: false,
  sortable: false,
  renderCell: ({ row }) => (row[field] ? <CheckIcon /> : ''),
});

export const stateManagementTableColumns = (): GridColDef<StateDTO>[] => [
  {
    field: 'name',
    headerName: 'state.operational-scheme',
    align: 'left',
    flex: 2,
  },
  {
    field: 'description',
    headerName: 'state-management.table.header.description',
    align: 'left',
    flex: 2,
  },
  {
    field: 'terminalState',
    headerName: 'state-management.table.header.terminal-state',
    align: 'left',
    flex: 2,
    renderCell: ({ row }) => (
      <span className={TERMINAL_STATE_CLASS_NAMES[row.terminalState] ?? ''}>
        <EnumTranslate data={row.terminalState} />
      </span>
    ),
  },
  {
    field: 'dayFrom',
    headerName: 'state-management.table.header.day-from',
    flex: 2,
  },
  {
    field: 'dayTo',
    headerName: 'state-management.table.header.day-to',
    flex: 2,
  },
  dayColumn('monday', 'state-management.table.header.monday'),
  dayColumn('tuesday', 'state-management.table.header.tuesday'),
  dayColumn('wednesday', 'state-management.table.header.wednesday'),
  dayColumn('thursday', 'state-management.table.header.thursday'),
  dayColumn('friday', 'state-management.table.header.friday'),
  dayColumn('saturday', 'state-management.table.header.saturday'),
  dayColumn('sunday', 'state-management.table.header.sunday'),
  {
    field: 'dateFrom',
    headerName: 'state-management.table.header.date-from',
    flex: 2,
  },
  {
    field: 'dateTo',
    headerName: 'state-management.table.header.date-to',
    flex: 2,
  },
  {
    field: 'groupNames',
    headerName: 'common.groups',
    align: 'left',
    flex: 2,
    valueGetter: (groupNames: string[]) =>
      Array.isArray(groupNames) ? groupNames.join(', ') : '',
  },
];

export const stateManagementRowActions =
  (
    hasWritePermission: boolean,
    onStateEdit: (id: number) => void,
    onRowDelete: (state: StateDTO) => void,
    t: TranslationFunction,
  ) =>
  (row: StateDTO) => (
    <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
      <Tooltip title={t('general.edit-row-data.button')}>
        <span>
          <IconButton
            disabled={!hasWritePermission}
            onClick={() => onStateEdit(Number(row.id))}
          >
            <Edit />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={t('general.delete-row-data.button')}>
        <span>
          <IconButton disabled={!hasWritePermission} onClick={() => onRowDelete(row)}>
            <Delete />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
